'use client';

import React, { useEffect, useRef, useState } from 'react';

const keyLocations = [
  {
    id: 'church',
    number: '1',
    name: 'St Petrox Church',
    note: 'Ceremony · 12:00',
    color: '#2C3E50',
    lat: 50.34227473333068,
    lng: -3.566364372086177,
    mapsQuery: 'St+Petrox+Church+Dartmouth',
    labelBelow: false,
  },
  {
    id: 'cove',
    number: '2',
    name: 'Castle Cove',
    note: 'Post-ceremony refreshments',
    color: '#C8B89A',
    lat: 50.34145608189964,
    lng: -3.5656146192656726,
    mapsQuery: 'Castle+Cove+Dartmouth',
    labelBelow: true,
  },
  {
    id: 'boathouse',
    number: '3',
    name: 'Gullsway Boathouse',
    note: 'Reception · 15:00',
    color: '#7A8C5E',
    lat: 50.34680140004612,
    lng: -3.5771414577019183,
    mapsQuery: 'Gullsway+Boathouse+Dartmouth',
    labelBelow: false,
  },
  {
    id: 'ferry',
    number: '4',
    name: 'Ferry Departs Dartmouth',
    note: 'Passenger ferry, approximately every 20 mins',
    color: '#4A90A4',
    lat: 50.35036797119165,
    lng: -3.5772246789810374,
    mapsQuery: 'Dartmouth+Castle+Ferry',
    labelBelow: false,
  },
  {
    id: 'galions',
    number: '5',
    name: 'Galions Quay',
    note: 'Friday evening gathering, 17:00–19:00',
    color: '#A0522D',
    lat: 50.34315009722705,
    lng: -3.569736151836803,
    mapsQuery: 'Galions+Quay+Castle+Rd+Dartmouth+TQ6+0JN',
    labelBelow: false,
  },
];

// Map centre and zoom
const MAP_CENTER = { lat: 50.3455, lng: -3.5710 };
const ZOOM = 15;

function lngToTileX(lng: number, zoom: number) {
  return ((lng + 180) / 360) * Math.pow(2, zoom);
}

function latToTileY(lat: number, zoom: number) {
  const latRad = (lat * Math.PI) / 180;
  return ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * Math.pow(2, zoom);
}

function latLngToPixel(
  lat: number,
  lng: number,
  centerLat: number,
  centerLng: number,
  zoom: number,
  mapWidth: number,
  mapHeight: number
) {
  const tileSize = 256;
  const centerX = lngToTileX(centerLng, zoom) * tileSize;
  const centerY = latToTileY(centerLat, zoom) * tileSize;
  const pointX = lngToTileX(lng, zoom) * tileSize;
  const pointY = latToTileY(lat, zoom) * tileSize;
  return {
    x: mapWidth / 2 + (pointX - centerX),
    y: mapHeight / 2 + (pointY - centerY),
  };
}

export default function ScheduleMap() {
  const mapRef = useRef<HTMLDivElement>(null);
  const [mapSize, setMapSize] = useState({ width: 800, height: 480 });
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const updateSize = () => {
      if (mapRef.current) {
        setMapSize({
          width: mapRef.current.offsetWidth,
          height: mapRef.current.offsetHeight,
        });
      }
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  // Build OSM tile grid
  const tileSize = 256;
  const centerTileX = lngToTileX(MAP_CENTER.lng, ZOOM);
  const centerTileY = latToTileY(MAP_CENTER.lat, ZOOM);
  const tilesX = Math.ceil(mapSize.width / tileSize) + 2;
  const tilesY = Math.ceil(mapSize.height / tileSize) + 2;
  const startTileX = Math.floor(centerTileX) - Math.floor(tilesX / 2);
  const startTileY = Math.floor(centerTileY) - Math.floor(tilesY / 2);
  const offsetX = mapSize.width / 2 - (centerTileX - Math.floor(centerTileX)) * tileSize - Math.floor(tilesX / 2) * tileSize;
  const offsetY = mapSize.height / 2 - (centerTileY - Math.floor(centerTileY)) * tileSize - Math.floor(tilesY / 2) * tileSize;

  const tiles: { tx: number; ty: number; x: number; y: number }[] = [];
  for (let i = 0; i < tilesX; i++) {
    for (let j = 0; j < tilesY; j++) {
      tiles.push({
        tx: startTileX + i,
        ty: startTileY + j,
        x: offsetX + i * tileSize,
        y: offsetY + j * tileSize,
      });
    }
  }

  return (
    <section className="bg-off-white py-24 px-8 md:px-16 xl:px-24 border-t border-border">
      <div className="max-w-screen-2xl mx-auto">
        <div className="mb-12">
          <p className="section-label mb-4">Getting Around</p>
          <h2 className="font-serif text-display-md text-faded-navy">Dartmouth &amp; Beyond</h2>
        </div>

        {/* Legend */}
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-8 mb-8 flex-wrap">
          {keyLocations?.map((loc) => (
            <a
              key={loc?.id}
              href={`https://www.google.com/maps/search/?api=1&query=${loc?.mapsQuery}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-3 group"
            >
              <div
                className="flex items-center justify-center rounded-full shrink-0 text-white font-bold text-sm"
                style={{
                  backgroundColor: loc?.color,
                  width: '30px',
                  height: '30px',
                  marginTop: '2px',
                }}
              >
                {loc?.number}
              </div>
              <div>
                <p className="font-sans text-sm text-faded-navy font-semibold leading-tight group-hover:underline">
                  {loc?.name}
                </p>
                <p className="font-sans text-xs text-stone mt-0.5">{loc?.note}</p>
              </div>
            </a>
          ))}
        </div>

        {/* Ferry photo */}
        <div className="mt-6 mb-2 flex flex-col sm:flex-row items-start gap-4">
          <div className="shrink-0">
            <img
              src="/assets/images/Screenshot_2026-04-12_at_16.49.30-1776008991968.png"
              alt="Dartmouth Castle passenger ferry crossing the River Dart"
              className="rounded-sm border border-border"
              style={{ width: '152px', height: '86px', objectFit: 'cover' }}
            />
          </div>
          <div>
            <p className="font-sans text-sm font-semibold text-faded-navy">Dartmouth Castle Ferry</p>
            <p className="font-sans text-xs text-stone mt-1 leading-relaxed">
              The passenger ferry departs from Dartmouth quayside approximately every 20 minutes and takes around 15 minutes to cross. Look for marker <span className="inline-flex items-center justify-center rounded-full text-white font-bold" style={{ backgroundColor: '#4A90A4', width: '16px', height: '16px', fontSize: '10px', display: 'inline-flex' }}>4</span> on the map.
            </p>
          </div>
        </div>

        {/* Map with custom markers */}
        <div
          ref={mapRef}
          className="w-full rounded-sm overflow-hidden border border-border relative"
          style={{ height: '480px', background: '#e8e0d8' }}
        >
          {/* OSM Tile layer */}
          {mounted && tiles.map((tile) => {
            const maxTile = Math.pow(2, ZOOM);
            const wrappedTx = ((tile.tx % maxTile) + maxTile) % maxTile;
            return (
              <img
                key={`${tile.tx}-${tile.ty}`}
                src={`https://tile.openstreetmap.org/${ZOOM}/${wrappedTx}/${tile.ty}.png`}
                alt=""
                style={{
                  position: 'absolute',
                  left: tile.x,
                  top: tile.y,
                  width: tileSize,
                  height: tileSize,
                  imageRendering: 'crisp-edges',
                  userSelect: 'none',
                  pointerEvents: 'none',
                }}
                draggable={false}
              />
            );
          })}

          {/* Markers */}
          {mounted && keyLocations.map((loc) => {
            const pos = latLngToPixel(
              loc.lat,
              loc.lng,
              MAP_CENTER.lat,
              MAP_CENTER.lng,
              ZOOM,
              mapSize.width,
              mapSize.height
            );
            return (
              <a
                key={loc.id}
                href={`https://www.google.com/maps/search/?api=1&query=${loc.mapsQuery}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  position: 'absolute',
                  left: pos.x,
                  top: pos.y,
                  transform: loc.labelBelow ? 'translate(-50%, -50%)' : 'translate(-50%, -100%)',
                  zIndex: 10,
                  textDecoration: 'none',
                }}
                title={loc.name}
              >
                {/* Pin shape */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  {/* Label above (default) */}
                  {!loc.labelBelow && (
                    <div
                      style={{
                        marginBottom: '4px',
                        backgroundColor: 'rgba(255,255,255,0.95)',
                        color: '#2C3E50',
                        fontSize: '11px',
                        fontWeight: 600,
                        padding: '2px 6px',
                        borderRadius: '3px',
                        whiteSpace: 'nowrap',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                      }}
                    >
                      {loc.name}
                    </div>
                  )}
                  <div
                    style={{
                      backgroundColor: loc.color,
                      color: '#fff',
                      borderRadius: '50% 50% 50% 0',
                      transform: 'rotate(-45deg)',
                      width: '36px',
                      height: '36px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
                      border: '2px solid #fff',
                    }}
                  >
                    <span
                      style={{
                        transform: 'rotate(45deg)',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        lineHeight: 1,
                      }}
                    >
                      {loc.number}
                    </span>
                  </div>
                  {/* Label below */}
                  {loc.labelBelow && (
                    <div
                      style={{
                        marginTop: '4px',
                        backgroundColor: 'rgba(255,255,255,0.95)',
                        color: '#2C3E50',
                        fontSize: '11px',
                        fontWeight: 600,
                        padding: '2px 6px',
                        borderRadius: '3px',
                        whiteSpace: 'nowrap',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                      }}
                    >
                      {loc.name}
                    </div>
                  )}
                </div>
              </a>
            );
          })}

          {/* OSM attribution */}
          <div
            style={{
              position: 'absolute',
              bottom: 4,
              right: 6,
              fontSize: '10px',
              color: '#555',
              background: 'rgba(255,255,255,0.7)',
              padding: '1px 4px',
              borderRadius: '2px',
              zIndex: 20,
            }}
          >
            © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer" style={{ color: '#555' }}>OpenStreetMap</a> contributors
          </div>
        </div>

        {/* Direct links to each location */}
        <div className="flex flex-wrap gap-4 mt-5">
          {keyLocations?.map((loc) => (
            <a
              key={loc?.id}
              href={`https://www.google.com/maps/search/?api=1&query=${loc?.mapsQuery}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-xs font-sans font-medium text-stone hover:text-faded-navy transition-colors"
            >
              <span
                className="inline-flex items-center justify-center rounded-full text-white font-bold"
                style={{ backgroundColor: loc?.color, width: '18px', height: '18px', fontSize: '10px' }}
              >
                {loc?.number}
              </span>
              {loc?.name} ↗
            </a>
          ))}
        </div>

        <p className="text-xs font-sans text-stone/50 mt-3">
          Click a location to open directions in Google Maps.
        </p>
      </div>
    </section>
  );
}